import { AlertCircle, Lock } from "lucide-react";

import type { Pagination } from "@/api/development_suite";
import { isDeniedError } from "@/components/development_suite/developmentSuiteQueries";
import { Button } from "@/components/ui/button";

/**
 * A failed load. A 403 means the suite or this municipality is closed to the
 * user, which a retry cannot fix, so it gets the server's reason and no button.
 */
export function RequestFailure({
  error,
  what,
  onRetry,
}: {
  error: unknown;
  what: string;
  onRetry: () => void;
}) {
  if (isDeniedError(error)) {
    return (
      <div role="alert" className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <p>
          {error instanceof Error ? error.message : "You do not have access."} Ask your team administrator if you
          need access.
        </p>
      </div>
    );
  }

  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-destructive/40 p-3 text-sm">
      <AlertCircle className="h-4 w-4 shrink-0 text-destructive" aria-hidden />
      <p className="flex-1">{what} could not be loaded. Check your connection and try again.</p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

export function ListPager({
  pagination,
  label,
  onPageChange,
}: {
  pagination: Pagination;
  label: string;
  onPageChange: (page: number) => void;
}) {
  if (pagination.total_pages <= 1) return null;

  return (
    <nav aria-label={`${label} pages`} className="flex items-center justify-between gap-2 pt-2 text-xs">
      <Button
        size="sm"
        variant="outline"
        disabled={pagination.current_page <= 1}
        onClick={() => onPageChange(pagination.current_page - 1)}
      >
        Previous
      </Button>
      <span className="text-muted-foreground">
        Page {pagination.current_page} of {pagination.total_pages}
      </span>
      <Button
        size="sm"
        variant="outline"
        disabled={pagination.current_page >= pagination.total_pages}
        onClick={() => onPageChange(pagination.current_page + 1)}
      >
        Next
      </Button>
    </nav>
  );
}
